import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ProfilePhoto } from '../components/ProfilePhoto';
import { GraduationCap, Users, Coins, BookOpen, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { classAPI } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

export default function JoinClass() {
  const { joinCode } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);
  const [studentId, setStudentId] = useState('');

  useEffect(() => {
    loadClassPreview();
  }, [joinCode]);

  const loadClassPreview = async () => {
    try {
      const res = await fetch(`${API}/api/classes/preview/${joinCode}`);
      if (!res.ok) throw new Error('Clase no encontrada');
      const data = await res.json();
      setClassInfo(data);
    } catch {
      setError('El enlace de invitación no es válido o la clase ya no existe.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!isAuthenticated) {
      // Save the join URL and redirect to login
      sessionStorage.setItem('pendingJoin', joinCode);
      navigate('/login');
      return;
    }

    if (user?.role !== 'student') {
      toast.error('Solo estudiantes pueden unirse a clases');
      return;
    }

    setJoining(true);
    try {
      await classAPI.join({ join_code: joinCode, student_id: studentId });
      toast.success(`¡Te uniste a ${classInfo.name}!`);
      navigate(`/class/${classInfo.id}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al unirse';
      if (msg.includes('Ya estás inscrito')) {
        toast.info('Ya estás inscrito en esta clase');
        navigate(`/class/${classInfo.id}`);
      } else {
        toast.error(msg);
      }
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="flex flex-col items-center py-12">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-lg font-heading font-semibold mb-2">Enlace no válido</h3>
            <p className="text-sm text-slate-500 text-center mb-6">{error}</p>
            <Button onClick={() => navigate('/dashboard')} variant="outline">Ir al Inicio</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-8">
      <Card className="max-w-lg w-full border-blue-200 shadow-lg" data-testid="join-class-card">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/30">
            <GraduationCap className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="font-heading text-xl sm:text-2xl">Te han invitado a una clase</CardTitle>
          <CardDescription>Revisa los detalles y únete para comenzar</CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Class Info */}
          <div className="p-4 bg-slate-50 rounded-xl space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
              <div className="min-w-0">
                <h3 className="font-heading font-bold text-slate-900 text-lg truncate" data-testid="class-name">
                  {classInfo.name}
                </h3>
                <p className="text-sm text-slate-500">{classInfo.semester}</p>
              </div>
            </div>
            
            {classInfo.description && (
              <p className="text-sm text-slate-600">{classInfo.description}</p>
            )}
            
            <div className="flex items-center gap-4 pt-2 border-t border-slate-200">
              <div className="flex items-center gap-2">
                <ProfilePhoto photoUrl={classInfo.professor_photo_url} name={classInfo.professor_name} size={28} />
                <span className="text-sm text-slate-700">{classInfo.professor_name}</span>
              </div>
              <Badge variant="outline" className="text-slate-600">
                <Users className="w-3 h-3 mr-1" />
                {classInfo.student_count} estudiantes
              </Badge>
            </div>
          </div>

          {/* CampusCoins Badge */}
          <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <Coins className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              <span className="font-medium">CampusCoins</span> - Gana coins y XP completando actividades
            </p>
          </div>

          {/* Join Actions */}
          {isAuthenticated && user?.role === 'student' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Tu ID de Estudiante (opcional)</Label>
                <Input
                  placeholder="Ej: A12345"
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  data-testid="join-student-id"
                />
              </div>
            </div>
          )}

          <Button 
            onClick={handleJoin} 
            disabled={joining}
            className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-base shadow-lg shadow-blue-500/20"
            data-testid="join-class-btn"
          >
            {joining ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Uniéndose...</>
            ) : isAuthenticated ? (
              <><ArrowRight className="w-5 h-5 mr-2" /> Unirme a la Clase</>
            ) : (
              <><ArrowRight className="w-5 h-5 mr-2" /> Iniciar Sesión para Unirme</>
            )}
          </Button>

          {!isAuthenticated && (
            <p className="text-xs text-slate-500 text-center">
              ¿No tienes cuenta? <button onClick={() => { sessionStorage.setItem('pendingJoin', joinCode); navigate('/register'); }} className="text-blue-600 hover:underline font-medium">Regístrate aquí</button>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
